import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from '../axios/axiosInstance';
import { featuresForCompanyContext } from 'constants/companyFeatures';

const readLocalCompanies = () => {
  try { return JSON.parse(localStorage.getItem('userCompanies') || '[]'); }
  catch { return []; }
};

const CompanySwitcher = ({ onSwitch }) => {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const [companies, setCompanies] = useState(readLocalCompanies);
  const ref = useRef(null);

  const currentCompanyId = localStorage.getItem('companyId');
  const current = companies.find(c => c.id === currentCompanyId) || companies[0];

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const refreshCompanies = async () => {
    try {
      const res = await axiosInstance.get('/auth/my-companies');
      const list = Array.isArray(res.data) ? res.data : (res.data?.companies || []);
      setCompanies(list);
      localStorage.setItem('userCompanies', JSON.stringify(list));
    } catch {
      setCompanies(readLocalCompanies());
    }
  };

  useEffect(() => {
    if (open) refreshCompanies();
  }, [open]);

  const role = localStorage.getItem('userRole');
  // Accountants/Company always see the switcher with 1+ companies (context + manage link).
  // SuperAdmin only when there are 2+ to switch between.
  if (!companies || companies.length === 0) return null;
  if (companies.length <= 1 && role !== 'Accountant' && role !== 'Company' && role !== 'Reseller') return null;

  const handleSwitch = async (companyId) => {
    if (companyId === currentCompanyId) { setOpen(false); return; }
    setSwitching(companyId);
    try {
      const res = await axiosInstance.post('/auth/switch-company', { companyId });
      localStorage.setItem('authToken', res.data.token);
      localStorage.setItem('companyId', companyId);
      const activeRole = localStorage.getItem('userRole') || res.data.role || role;
      localStorage.setItem(
        'userFeatures',
        JSON.stringify(featuresForCompanyContext(activeRole, res.data.features))
      );
      setOpen(false);
      if (onSwitch) onSwitch(companyId);
      else window.location.href = '/dashboard';
    } catch (err) {
      console.error('Switch company failed', err);
    } finally {
      setSwitching(null);
    }
  };

  const displayName = (c) => c.tradeName || c.legalName || c.id;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: '#F0F7FF', border: '1px solid #f0d0b8',
          borderRadius: 8, padding: '6px 12px',
          cursor: 'pointer', fontSize: 13, fontWeight: 600,
          color: '#1B75F0', maxWidth: 220, whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}
        title={current ? displayName(current) : 'Select Company'}
      >
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {current ? displayName(current) : 'Select Company'}
        </span>
        <svg viewBox="0 0 24 24" fill="none" width="10" height="10" style={{ flexShrink: 0 }}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0,
          background: '#fff', borderRadius: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.14)',
          minWidth: 240, maxWidth: 320, zIndex: 1001,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Switch Company
            </div>
          </div>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {companies.map(c => {
              const isActive = c.id === currentCompanyId;
              const isLoading = switching === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => handleSwitch(c.id)}
                  disabled={isLoading}
                  style={{
                    width: '100%', padding: '10px 14px',
                    background: isActive ? '#F0F7FF' : 'none',
                    border: 'none', borderBottom: '1px solid #f8f8f8',
                    textAlign: 'left', cursor: isActive ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#fafafa'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'none'; }}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                    background: isActive ? 'linear-gradient(135deg, #1B75F0, #5B9BF5)' : '#f3f4f6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                    color: isActive ? '#fff' : '#6b7280',
                  }}>
                    {displayName(c).charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      color: isActive ? '#1B75F0' : '#333',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {displayName(c)}
                    </div>
                    {isActive && (
                      <div style={{ fontSize: 11, color: '#1B75F0', marginTop: 1 }}>Current</div>
                    )}
                  </div>
                  {isLoading && (
                    <div style={{
                      width: 14, height: 14, border: '2px solid #1B75F0',
                      borderTopColor: 'transparent', borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite', flexShrink: 0,
                    }} />
                  )}
                  {isActive && !isLoading && (
                    <svg viewBox="0 0 24 24" fill="none" width="14" height="14" style={{ flexShrink: 0 }}>
                      <path d="M5 13l4 4L19 7" stroke="#1B75F0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
          {(role === 'Accountant' || role === 'Company') && (
            <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 14px' }}>
              <a
                href="/dashboard/my-companies"
                onClick={(e) => { e.preventDefault(); setOpen(false); window.location.href = '/dashboard/my-companies'; }}
                style={{ fontSize: 12, fontWeight: 600, color: '#1B75F0', textDecoration: 'none' }}
              >
                + Manage / Add Companies
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanySwitcher;
